import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Box,
  TablePagination,
  Skeleton,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Snackbar,
  Typography,
  Grid
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Warning as WarningIcon,
  FilterList as FilterIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { InventoryItem, InventoryFilter } from '../types';
import inventoryService from '../services/inventoryService';

interface InventoryTableProps {
  searchTerm?: string;
  onViewDetails?: (sku: string) => void;
}



export const InventoryTable: React.FC<InventoryTableProps> = ({ searchTerm, onViewDetails }) => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  const [filters, setFilters] = useState<InventoryFilter>({
    searchTerm,
    page: 1,
    pageSize: 25,
    sortBy: 'sku',
    sortOrder: 'asc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load inventory data
  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filter: InventoryFilter = {
        ...filters,
        searchTerm,
        page: page + 1,
        pageSize: rowsPerPage
      };

      const response = await inventoryService.getInventoryItems(filter);
      setItems(response.items);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading inventory:', error);
      setError('Failed to load inventory data. Using mock data for development.');
      // Use mock data if API fails
      setItems(getMockInventoryItems());
      setTotal(100);
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm, page, rowsPerPage]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  // Handle row selection
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(new Set(items.map(item => item.SKU)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (sku: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(sku)) {
      newSelected.delete(sku);
    } else {
      newSelected.add(sku);
    }
    setSelectedItems(newSelected);
  };

  // Handle inline editing




  // Handle bulk operations
  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedItems.size} items?`)) {
      try {
        // In a real implementation, you would call a bulk delete API
        console.log('Bulk delete:', Array.from(selectedItems));
        setSuccess(`Deleted ${selectedItems.size} items`);
        setSelectedItems(new Set());
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        setError('Failed to delete items');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedItems.size === 0) return;
    
    // In a real implementation, you would open a bulk update modal
    console.log('Bulk update:', Array.from(selectedItems));
    setSuccess(`Updated ${selectedItems.size} items`);
    setTimeout(() => setSuccess(null), 3000);
  };

  // Handle sorting
  const handleSort = (field: keyof InventoryItem) => {
    // Map InventoryItem keys to InventoryFilter sortBy values
    const sortByMap: Record<keyof InventoryItem, 'sku' | 'product' | 'quantity' | 'value' | 'turnover'> = {
      SKU: 'sku',
      Product: 'product',
      QuantityOnHand: 'quantity',
      Cost: 'value',
      TurnoverRate: 'turnover',
      // Add other fields with default mapping
      Description: 'sku',
      UnitWeight: 'sku',
      Location: 'sku',
      Hazmat: 'sku',
      QuantityAllocated: 'sku',
      QuantityAvailable: 'sku',
      ReorderLevel: 'sku',
      ReorderQuantity: 'sku',
      RetailPrice: 'sku',
      SalePrice: 'sku',
      Brand: 'sku',
      Category: 'sku',
      SubCategory: 'sku',
      ProductType: 'sku',
      SupplierID: 'sku',
      SupplierSKU: 'sku',
      LeadTimeDays: 'sku',
      LastSoldDate: 'sku',
      LastReceivedDate: 'sku',
      DaysOfSupply: 'sku',
      IsActive: 'sku',
      IsDiscontinued: 'sku',
      ExpirationDate: 'sku'
    };

    const sortByValue = sortByMap[field];
    const newSortOrder = filters.sortBy === sortByValue && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters(prev => ({
      ...prev,
      sortBy: sortByValue,
      sortOrder: newSortOrder
    }));
  };

  // Get stock status
  const getStockStatus = (item: InventoryItem) => {
    if (item.QuantityOnHand === 0) return { label: 'Out of Stock', color: 'error' as const };
    if (item.QuantityOnHand <= (item.ReorderLevel || 10)) return { label: 'Low Stock', color: 'warning' as const };
    if (item.QuantityOnHand > (item.ReorderLevel || 10) * 3) return { label: 'Overstock', color: 'info' as const };
    return { label: 'In Stock', color: 'success' as const };
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  if (loading) {
    return (
      <Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={60} sx={{ my: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Error and Success Messages */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">{success}</Alert>
      </Snackbar>

      {/* Bulk Actions */}
      {selectedItems.size > 0 && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: 'primary.light', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="white" gutterBottom>
            {selectedItems.size} items selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={handleBulkUpdate}
            >
              Bulk Update
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleBulkDelete}
            >
              Bulk Delete
            </Button>
          </Box>
        </Box>
      )}

      {/* Filters */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<FilterIcon />}
          onClick={() => setShowFilters(!showFilters)}
          variant="outlined"
          size="small"
        >
          Filters
        </Button>
        
        {showFilters && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || []}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
                    multiple
                  >
                    <MenuItem value="in-stock">In Stock</MenuItem>
                    <MenuItem value="low-stock">Low Stock</MenuItem>
                    <MenuItem value="out-of-stock">Out of Stock</MenuItem>
                    <MenuItem value="overstock">Overstock</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Hazmat</InputLabel>
                  <Select
                    value={filters.hazmat === undefined ? '' : filters.hazmat}
                    onChange={(e) => setFilters(prev => ({ ...prev, hazmat: e.target.value === '' ? undefined : e.target.value === 'true' }))}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Hazmat Only</MenuItem>
                    <MenuItem value="false">Non-Hazmat Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Min Value"
                  type="number"
                  value={filters.minValue || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, minValue: e.target.value ? Number(e.target.value) : undefined }))}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Max Value"
                  type="number"
                  value={filters.maxValue || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxValue: e.target.value ? Number(e.target.value) : undefined }))}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedItems.size > 0 && selectedItems.size < items.length}
                    checked={selectedItems.size > 0 && selectedItems.size === items.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('SKU')}>
                    SKU
                    <SortIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Product')}>
                    Product
                    <SortIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                  </Box>
                </TableCell>
                <TableCell>Brand</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('QuantityOnHand')}>
                    Qty on Hand
                    <SortIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                  </Box>
                </TableCell>
                <TableCell align="right">Available</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('Cost')}>
                    Cost
                    <SortIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                  </Box>
                </TableCell>
                <TableCell align="right">Value</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const status = getStockStatus(item);
                
                return (
                  <TableRow key={item.SKU} hover selected={selectedItems.has(item.SKU)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.has(item.SKU)}
                        onChange={() => handleSelectItem(item.SKU)}
                      />
                    </TableCell>
                    <TableCell>{item.SKU}</TableCell>
                    <TableCell>
                      <Box>
                        <Box fontWeight="medium">{item.Product}</Box>
                        {item.Hazmat && (
                          <Chip 
                            icon={<WarningIcon />} 
                            label="Hazmat" 
                            size="small" 
                            color="error" 
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{item.Brand || '-'}</TableCell>
                    <TableCell align="right">
                      {item.QuantityOnHand}
                    </TableCell>
                    <TableCell align="right">{item.QuantityAvailable || item.QuantityOnHand}</TableCell>
                    <TableCell>
                      <Chip label={status.label} color={status.color} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.Cost)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency((item.Cost || 0) * item.QuantityOnHand)}
                    </TableCell>
                    <TableCell>
                      {item.Location || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => onViewDetails?.(item.SKU)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

// Mock data for development
function getMockInventoryItems(): InventoryItem[] {
  return [
    {
      SKU: 'CHI-001',
      Product: 'CHI Ceramic Flat Iron',
      Brand: 'CHI',
      QuantityOnHand: 45,
      QuantityAvailable: 40,
      ReorderLevel: 20,
      Cost: 65.00,
      RetailPrice: 129.99,
      Location: 'A1-B3',
      Hazmat: false,
      IsActive: true
    },
    {
      SKU: 'OPI-NL-001',
      Product: 'OPI Nail Lacquer - Big Apple Red',
      Brand: 'OPI',
      QuantityOnHand: 8,
      QuantityAvailable: 8,
      ReorderLevel: 15,
      Cost: 4.50,
      RetailPrice: 10.50,
      Location: 'C2-A1',
      Hazmat: true,
      IsActive: true
    },
    {
      SKU: 'REDKEN-SH-001',
      Product: 'Redken All Soft Shampoo 300ml',
      Brand: 'Redken',
      QuantityOnHand: 0,
      QuantityAvailable: 0,
      ReorderLevel: 30,
      Cost: 12.00,
      RetailPrice: 24.00,
      Location: 'B3-C2',
      Hazmat: false,
      IsActive: true
    }
  ];
}
